import { nanoid } from 'nanoid';
import { INodePayload } from '../types/models-update';
import { IAddNodes, IDeleteNodes } from '../editor-block/store/types/mutations';
import { ICell, IEditorBlock, INode, INodeBase, IRow, ISection, ITableStyle } from '@/editor/types';
import { IDocumentPage } from '@/types/document-page';
import { EDocumentPageType } from '@/enums/document-page-type';
import { EDividerType, EImageSize, ENodeType } from '@/editor/types/enums';
import { fallbackTableContent, fallbackTextContent } from '@/editor/editor-block/constants';
import { IDealroomState } from '@/dealroom/store/dealroom.store';

export class EditorHelper {
  static createTempBlock(pageNum: number): IDocumentPage {
    const tempId = nanoid();
    return {
      pageType: EDocumentPageType.Editor,
      pageNum,
      id: tempId,
      loading: true,
      editorBlock: {
        contentId: tempId + '-content',
        content: {
          id: tempId + '-content',
          sections: [
            {
              id: tempId + '-section',
              rows: [
                {
                  id: tempId + '-row',
                  cells: [
                    {
                      id: tempId + '-cell',
                      nodes: [
                        this.getNewNode({ nodeType: ENodeType.Text, nodeId: tempId + '-node' }),
                      ],
                    },
                  ],
                },
              ],
            },
          ],
        },
      } as IEditorBlock,
    };
  }

  static getNewNode(payload: INodePayload): INode {
    const {
      tableStyle,
      dividerType,
      imageSize,
      caption,
      heading,
      content,
      imageId,
      srcUrl,
      linkUrl,
      fields,
      nodeType: type,
      nodeId: id,
    } = payload;

    const baseNode: INodeBase = {
      id,
      type,
    };

    switch (type) {
      case ENodeType.Text:
        return {
          ...baseNode,
          content: content || fallbackTextContent,
          fields: fields || [],
        };

      case ENodeType.Image:
        return {
          ...baseNode,
          imageId: imageId || '',
          srcUrl: srcUrl || '',
          linkUrl: linkUrl || '',
          imageSize: imageSize || EImageSize.Original,
          caption: caption || '',
          heading: heading || '',
        };

      case ENodeType.Table:
        return {
          ...baseNode,
          content: content || fallbackTableContent,
          tableStyle: tableStyle || ({} as ITableStyle),
          fields: fields || [],
        };

      case ENodeType.Divider:
        return {
          ...baseNode,
          dividerType: dividerType || EDividerType.Thin,
        };

      default:
        return undefined;
    }
  }

  static addNodes(
    state: IDealroomState,
    {
      contentId,
      targetSectionId,
      targetRowId,
      targetCellId,
      targetNodeId,
      append = true,
      nodes,
    }: IAddNodes
  ): IDocumentPage[] {
    return state.document.pages.map((block: IDocumentPage) => {
      if (block.editorBlock?.contentId === contentId) {
        return {
          ...block,
          editorBlock: {
            ...block.editorBlock,
            content: {
              ...block.editorBlock.content,
              sections: block.editorBlock.content.sections.map((section: ISection) => {
                if (section.id === targetSectionId) {
                  return {
                    ...section,
                    rows: section.rows.map((row: IRow) => {
                      if (row.id === targetRowId) {
                        return {
                          ...row,
                          cells: row.cells.map((cell: ICell) => {
                            if (cell.id === targetCellId) {
                              const index: number = cell.nodes.findIndex(
                                (node: INode) => node.id === targetNodeId
                              );

                              if (index === -1) {
                                return {
                                  ...cell,
                                  nodes: [...cell.nodes, ...nodes],
                                };
                              }

                              return {
                                ...cell,
                                ...cell.nodes.splice(index + Number(!!append), 0, ...nodes),
                              };
                            }
                            return cell;
                          }),
                        };
                      }
                      return row;
                    }),
                  };
                }
                return section;
              }),
            },
          },
        };
      }
      return block;
    });
  }

  static getDocumentPageById(state: IDealroomState, pageId: string) {
    return this.deepCopy(state?.document?.pages.find(({ id }: IDocumentPage) => id === pageId));
  }

  static deleteNodes(
    state: IDealroomState,
    { contentId, targetSectionId, targetRowId, targetCellId, targetNodeIds }: IDeleteNodes
  ): IDocumentPage[] {
    return state.document.pages.map((block: IDocumentPage) => {
      if (block.editorBlock?.contentId === contentId) {
        return {
          ...block,
          editorBlock: {
            ...block.editorBlock,
            content: {
              ...block.editorBlock.content,
              sections: block.editorBlock.content.sections.map((section: ISection) => {
                if (section.id === targetSectionId) {
                  return {
                    ...section,
                    rows: section.rows.map((row: IRow) => {
                      if (row.id === targetRowId) {
                        return {
                          ...row,
                          cells: row.cells.map((cell: ICell) => {
                            if (cell.id === targetCellId) {
                              return {
                                ...cell,
                                nodes: cell.nodes.filter(
                                  ({ id }: INode) => !targetNodeIds.includes(id)
                                ),
                              };
                            }
                            return cell;
                          }),
                        };
                      }
                      return row;
                    }),
                  };
                }
                return section;
              }),
            },
          },
        };
      }
      return block;
    });
  }

  static mergeDeep(target, source) {
    const isObject = obj => obj && typeof obj === 'object';
    const newTarget = { ...target };
    if (!isObject(newTarget) || !isObject(source)) {
      return source;
    }

    Object.keys(source).forEach(key => {
      const targetValue = newTarget[key];
      const sourceValue = source[key];

      if (Array.isArray(targetValue) && Array.isArray(sourceValue)) {
        newTarget[key] = sourceValue;
      } else if (isObject(targetValue) && isObject(sourceValue)) {
        newTarget[key] = this.mergeDeep(Object.assign({}, targetValue), sourceValue);
      } else {
        newTarget[key] = sourceValue;
      }
    });

    return newTarget;
  }

  static deepCopy(src, _visited?, _copiesVisited?) {
    if (src === null || typeof src !== 'object') {
      return src;
    }
    //Honor native/custom clone methods
    if (typeof src.clone == 'function') {
      return src.clone(true);
    }
    if (src instanceof Date) {
      return new Date(src.getTime());
    }
    if (src instanceof RegExp) {
      return new RegExp(src);
    }
    //DOM Element
    if (src.nodeType && typeof src.cloneNode == 'function') {
      return src.cloneNode(true);
    }
    // Initialize the visited objects arrays if needed.
    // This is used to detect cyclic references.
    if (_visited === undefined) {
      _visited = [];
      _copiesVisited = [];
    }
    // Check if this object has already been visited
    for (let index = 0; index < _visited.length; index++) {
      // If so, get the copy we already made
      if (src === _visited[index]) {
        return _copiesVisited[index];
      }
    }
    //Array
    if (Object.prototype.toString.call(src) == '[object Array]') {
      //[].slice() by itself would soft clone
      const ret = src.slice();
      //add it to the visited array
      _visited.push(src);
      _copiesVisited.push(ret);
      let index = ret.length;
      while (index--) {
        ret[index] = this.deepCopy(ret[index], _visited, _copiesVisited);
      }
      return ret;
    }
    //If we've reached here, we have a regular object
    //make sure the returned object has the same prototype as the original
    const proto = Object.getPrototypeOf(src);
    const dest = Object.create(proto);
    //add this object to the visited array
    _visited.push(src);
    _copiesVisited.push(dest);
    for (const key in src) {
      dest[key] = this.deepCopy(src[key], _visited, _copiesVisited);
    }
    return dest;
  }
}
